import { BUSINESS, HOMEPAGE_FAQS, SITE_URL } from './businessConfig';

/**
 * Pure functions that return JSON-LD payloads ready to be serialized into a
 * <script type="application/ld+json"> block. Each builder returns either a
 * single object or an array (graph) — both formats validate against Google's
 * Rich Results Test.
 *
 * No public-facing strings depend on these payloads; they're invisible to
 * the user but visible to crawlers.
 */

const baseLocation = () => ({
  '@type': 'PostalAddress',
  streetAddress: BUSINESS.address.streetAddress,
  addressLocality: BUSINESS.address.addressLocality,
  addressRegion: BUSINESS.address.addressRegion,
  postalCode: BUSINESS.address.postalCode,
  addressCountry: BUSINESS.address.addressCountry,
});

const openingHoursSpec = () =>
  BUSINESS.openingHours.map((h) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: h.dayOfWeek,
    opens: h.opens,
    closes: h.closes,
  }));

/**
 * Restaurant + LocalBusiness combined — schema.org allows Restaurant to be
 * a sub-type of LocalBusiness, so emitting Restaurant alone is enough for
 * Google to treat it as a local entity. We add explicit LocalBusiness
 * @type via a graph entry to maximise rich-result coverage.
 */
export const restaurantSchema = () => ({
  '@context': 'https://schema.org',
  '@type': ['Restaurant', 'LocalBusiness', 'FoodEstablishment'],
  '@id': `${SITE_URL}/#restaurant`,
  name: BUSINESS.legalName,
  alternateName: [BUSINESS.name, 'Cravin Crispy Forfar', 'Cravin Crrispy Takeaway'],
  description: BUSINESS.description,
  url: BUSINESS.url,
  menu: BUSINESS.menuUrl,
  sameAs: BUSINESS.sameAs,
  image: BUSINESS.logoUrl,
  logo: BUSINESS.logoUrl,
  telephone: BUSINESS.telephone,
  email: BUSINESS.email,
  priceRange: BUSINESS.priceRange,
  servesCuisine: BUSINESS.servesCuisine,
  address: baseLocation(),
  geo: {
    '@type': 'GeoCoordinates',
    latitude: BUSINESS.geo.latitude,
    longitude: BUSINESS.geo.longitude,
  },
  openingHoursSpecification: openingHoursSpec(),
  hasMap: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
    `${BUSINESS.legalName} ${BUSINESS.address.postalCode}`
  )}`,
  areaServed: BUSINESS.areaServed.map((a) => ({ '@type': 'Place', name: a })),
  acceptsReservations: false,
  paymentAccepted: 'Cash, Credit Card, Debit Card',
  // Schema.org delivery / collection availability — surfaced in rich results
  // for "near me" queries.
  hasDeliveryMethod: [
    ...(BUSINESS.hasDelivery
      ? [{ '@type': 'DeliveryMethod', name: 'Home delivery' }]
      : []),
    ...(BUSINESS.hasTakeaway
      ? [{ '@type': 'DeliveryMethod', name: 'Collection / Takeaway' }]
      : []),
  ],
  potentialAction: {
    '@type': 'OrderAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/`,
      inLanguage: 'en-GB',
      actionPlatform: [
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/MobileWebPlatform',
      ],
    },
    deliveryMethod: [
      'http://purl.org/goodrelations/v1#DeliveryModeOwnFleet',
      'http://purl.org/goodrelations/v1#DeliveryModePickUp',
    ],
  },
});

/**
 * WebSite schema — enables Google sitelinks search box and reinforces the
 * canonical domain in SERPs.
 */
export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${SITE_URL}/#website`,
  url: SITE_URL,
  name: BUSINESS.legalName,
  description: BUSINESS.description,
  inLanguage: 'en-GB',
  publisher: { '@id': `${SITE_URL}/#restaurant` },
});

/**
 * Menu schema built from the live menu fetched at runtime. Each category
 * becomes a MenuSection; each item becomes a MenuItem with price (GBP).
 * Falls back to a minimal Menu node if no items are supplied.
 */
export const menuSchema = (menuItems = []) => {
  // Group items by category for MenuSection.
  const byCategory = menuItems.reduce((acc, item) => {
    const cat = item.category || 'Menu';
    (acc[cat] = acc[cat] || []).push(item);
    return acc;
  }, {});

  const sections = Object.entries(byCategory).map(([category, items]) => ({
    '@type': 'MenuSection',
    name: category,
    hasMenuItem: items.map((it) => ({
      '@type': 'MenuItem',
      name: it.name,
      description: it.description || undefined,
      offers: {
        '@type': 'Offer',
        price: Number(it.price || 0).toFixed(2),
        priceCurrency: 'GBP',
      },
    })),
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    '@id': `${SITE_URL}/#menu`,
    name: `${BUSINESS.legalName} Menu`,
    inLanguage: 'en-GB',
    ...(sections.length ? { hasMenuSection: sections } : {}),
  };
};

/**
 * BreadcrumbList for the current page path.
 * `crumbs` is an array of { name, url } in order, root → current.
 */
export const breadcrumbSchema = (crumbs = []) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: crumbs.map((c, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: c.name,
    item: c.url,
  })),
});

/**
 * FAQPage — mainEntity mirrors the visible FAQ block on the homepage
 * WORD FOR WORD. Google penalises FAQPage schema when the answer text
 * disagrees with the rendered HTML, so both read from the same
 * HOMEPAGE_FAQS array in businessConfig.js.
 *
 * Accepts an optional `faqs` argument for pages that want a different
 * set (e.g. a dedicated /faq page in the future). Defaults to the
 * homepage list.
 */
export const faqSchema = (faqs = HOMEPAGE_FAQS) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  '@id': `${SITE_URL}/#faq`,
  mainEntity: faqs.map(({ q, a }) => ({
    '@type': 'Question',
    name: q,
    acceptedAnswer: { '@type': 'Answer', text: a },
  })),
});
